import { cva, type VariantProps } from 'class-variance-authority';
import {
  disabledState,
  focusRing,
  transitionBase,
  uiMarker,
  uiMarkerText,
} from './shared';

/**
 * TYPOGRAPHY
 *
 * `variant` picks a role from the type scale in tokens.css. `tone` picks a
 * colour. They are deliberately independent — a caption can be destructive, a
 * page title can be muted — so you never need a one-off class to combine them.
 *
 * To retune a size or weight, edit the matching --type-* token rather than the
 * class here; that keeps every use of the role in step.
 */
export const textRecipe = cva([uiMarkerText, ''], {
  variants: {
    variant: {
      /**
       * Keep whatever size the surrounding text sets, and style only what is
       * asked for.
       *
       * For an inline run inside a sentence — a bolded word, a muted aside —
       * where forcing a size is wrong. Without this, colouring three words
       * inside a caption silently resized them to `body`, which is why 234
       * spans in the app style themselves by hand instead of using Text.
       */
      inherit: '',
      display: 'font-sans text-display',
      h1: 'font-sans text-h1',
      h2: 'font-sans text-h2',
      h3: 'font-sans text-h3',
      h4: 'font-sans text-h4',
      /**
       * The rule over a block of a detail card — "Details", "What failed".
       * 20px medium, tight: it sits between h3 and h2 and no role on the
       * scale offers it. The call site mutes it.
       */
      section: 'font-sans text-xl font-medium leading-6 tracking-tight',
      'body-lg': 'font-sans text-body-lg',
      'body-lg-strong': 'font-sans text-body-lg-strong',
      body: 'font-sans text-body',
      /** Emphasised body — a sub-heading inside a card, a highlighted value. */
      'body-strong': 'font-sans text-body-strong',
      'body-sm': 'font-sans text-body-sm',
      'body-sm-strong': 'font-sans text-body-sm-strong',
      label: 'font-sans text-label',
      caption: 'font-sans text-caption',
      'caption-strong': 'font-sans text-caption-strong',
      overline: 'font-sans text-overline uppercase',
      code: 'font-mono text-code',
    },
    tone: {
      /** Inherit whatever colour the surrounding surface sets. */
      inherit: '',
      /** Primary reading colour. */
      default: 'text-foreground',
      /** Secondary detail, helper text, timestamps. */
      muted: 'text-muted-foreground',
      /** Errors and destructive consequences. */
      destructive: 'text-destructive',
      success: 'text-success',
      warning: 'text-warning',
      info: 'text-info',
      /** For text sitting on a filled primary surface. */
      'on-primary': 'text-primary-foreground',
    },
    /**
     * @deprecated Pick a role instead — `body-strong`, `body-sm-strong`,
     * `caption-strong` — or a heading.
     *
     * A role bundles size, leading, tracking and weight into one decision, and
     * this prop lets a call site break that. It was used at 227 of 620 sites:
     * 41 restating the weight the role already had, and 186 inventing
     * combinations the scale did not offer — 93 of which were the same missing
     * role, emphasised small text. Those are now named roles.
     *
     * What is left is text at 700 that is really a heading or a headline
     * figure, which wants a heading role rather than a bolder body. Kept until
     * those are re-homed, not because the prop is a good idea.
     */
    weight: {
      inherit: '',
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    /** Clamp to a single line with an ellipsis. */
    truncate: {
      true: 'truncate',
      false: '',
    },
    /** Render digits at a fixed width so columns of numbers line up. */
    numeric: {
      true: 'tabular-nums',
      false: '',
    },
    /**
     * Honour the newlines in the string. For one run of text with a break
     * in it — "Welcome to\nRelay Tow", a date over its time — where the app
     * was reaching for `<br />`. Join the lines with `\n` and say so here;
     * the text stays one string, which is what a translator or a test wants.
     */
    preserveLines: {
      true: 'whitespace-pre-line',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'body',
    tone: 'default',
    weight: 'inherit',
    truncate: false,
    numeric: false,
    preserveLines: false,
  },
});

export type TextRecipeProps = VariantProps<typeof textRecipe>;

/**
 * Which HTML element each role renders as by default. Semantics matter for
 * screen readers and for SEO on the public pages, so the mapping is explicit
 * rather than "everything is a div". Override per-use with the `as` prop.
 */
export const textDefaultElement = {
  /** A modifier on surrounding text, so it adds no block-level element. */
  inherit: 'span',
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  section: 'h3',
  'body-lg': 'p',
  'body-lg-strong': 'p',
  body: 'p',
  'body-strong': 'p',
  'body-sm': 'p',
  'body-sm-strong': 'p',
  label: 'span',
  caption: 'p',
  'caption-strong': 'p',
  overline: 'p',
  code: 'code',
} as const;

/**
 * An inline link in running text.
 *
 * Button's `link` variant is a control: it has a height, it is a flex row, it
 * sits beside other buttons. This is the other thing — "Forgot your password?",
 * a support address, the terms link under a form — which the sign-in flow was
 * writing nine times as `underline underline-offset-4 hover:text-foreground`
 * with the weight and colour picked fresh each time.
 *
 * Inherits its size: it is part of a sentence, and the sentence decides.
 */
export const textLinkRecipe = cva(
  [
    uiMarker,
    'inline cursor-pointer rounded-sm underline underline-offset-4',
    // The button form is a <button>, which browsers centre and lay out as an
    // inline-block even with `inline`. Take the alignment of the text around
    // it, so a wrapped address in a right-aligned cell stays right-aligned.
    '[text-align:inherit]',
    transitionBase,
    focusRing,
    // Only the button form can be disabled — "send it again" while it is
    // sending. An anchor never matches :disabled, so this costs it nothing.
    disabledState,
  ],
  {
    variants: {
      tone: {
        default: 'font-medium text-foreground hover:text-foreground/80',
        muted: 'text-muted-foreground hover:text-foreground',
        primary: 'font-medium text-primary hover:text-primary/80',
        /** Take the colour of the surrounding text; only the underline says "link". */
        inherit: 'hover:opacity-80',
      },
    },
    defaultVariants: { tone: 'default' },
  }
);

export type TextLinkRecipeProps = VariantProps<typeof textLinkRecipe>;
